/**
 * Document Renderer
 * Handles rendering of documents with formatted HTML or plain text
 */

import DOMPurify from 'dompurify';
import { PageLayoutProcessor } from './page-layout-processor';
import type { LoadedDocument } from '../file-loader';
import { logger } from '../../core/logger';

export interface DocumentRendererOptions {
  uiManager: any; // UIManager instance for translations
}

export class DocumentRenderer {
  private pageLayoutProcessor: PageLayoutProcessor;

  constructor(options: DocumentRendererOptions) {
    this.pageLayoutProcessor = new PageLayoutProcessor({
      uiManager: options.uiManager,
    });
  }

  /**
   * Render document to a container element
   * @param docData - Document data to render
   * @param contentElement - Container element to render into
   */
  render(docData: LoadedDocument | any, contentElement: HTMLElement): void {
    // Clear existing content
    contentElement.innerHTML = '';

    // Check if we have formatted HTML from docling (for DOCX/ODT/PDF files)
    const hasFormattedHTML =
      docData.formatParseResult &&
      docData.formatParseResult.html &&
      docData.formatParseResult.html.trim().length > 0;

    if (hasFormattedHTML) {
      this.renderFormatted(docData, contentElement);
    } else {
      this.renderPlain(docData, contentElement);
    }
  }

  /**
   * Render formatted HTML document
   * @param docData - Document data
   * @param contentElement - Container to append content to
   */
  renderFormatted(docData: LoadedDocument, contentElement: HTMLElement): void {
    const container = this.createFormattedContainer(docData);
    const hasImages = this.hasImgTags(docData);

    this.processDocumentStructure(container, docData, hasImages);
    contentElement.appendChild(container);
  }

  /**
   * Render plain text document
   * @param docData - Document data
   * @param contentElement - Container to append content to
   */
  renderPlain(docData: LoadedDocument, contentElement: HTMLElement): void {
    // Split plain text into paragraphs so that rule highlighting and
    // entity extraction can match individual blocks via token overlap,
    // rather than searching one giant text node.
    const content = docData.content;
    const paragraphs = content.split(/\n\s*\n/);
    const fragment = document.createDocumentFragment();
    for (const para of paragraphs) {
      const trimmed = para.trim();
      if (!trimmed) continue;
      const p = document.createElement('p');
      p.textContent = trimmed;
      fragment.appendChild(p);
    }
    contentElement.appendChild(fragment);
  }

  /**
   * Create formatted content container with cleaned HTML
   * @param docData - Document data
   */
  private createFormattedContainer(docData: LoadedDocument): HTMLElement {
    const container = document.createElement('div');
    container.className = 'docling-formatted-content';
    container.style.maxWidth = '100%';
    container.style.overflowX = 'auto';

    // #159: sanitise straight into nodes. Serialising to a string and parsing
    // it again with innerHTML cost ~30% on a large document. Docling's <style>
    // blocks go with FORBID_TAGS (their CSS text too, a FORBID_CONTENTS default).
    container.append(
      DOMPurify.sanitize(docData.formatParseResult?.html || '', {
        RETURN_DOM_FRAGMENT: true,
        FORBID_TAGS: ['style'],
      })
    );

    this.setupFormattedContainer(container);
    return container;
  }

  /**
   * Setup formatted container with proper styling
   * @param container - Container element
   */
  private setupFormattedContainer(container: HTMLElement): void {
    // Remove problematic inline width styles from docling HTML. Only elements
    // with a width attribute or inline style can have either (#159).
    const allElements = container.querySelectorAll('[width], [style]');
    allElements.forEach((el) => {
      const htmlEl = el as HTMLElement;
      // Remove width attributes that might cause overflow
      if (htmlEl.hasAttribute('width')) {
        htmlEl.removeAttribute('width');
      }
      // Check for inline width styles
      const inlineWidth = htmlEl.style.width;
      if (
        inlineWidth &&
        (inlineWidth.includes('px') || parseInt(inlineWidth) > 800)
      ) {
        htmlEl.style.width = '';
        htmlEl.style.maxWidth = '100%';
      }
    });

    // Wrap all tables in a scrollable wrapper
    const tables = container.querySelectorAll('table');
    tables.forEach((table) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'table-wrapper';
      table.parentNode?.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
  }

  /**
   * Check if document HTML contains image tags
   * @param docData - Document data
   */
  private hasImgTags(docData: LoadedDocument): boolean {
    return docData.formatParseResult?.html?.includes('<img') || false;
  }

  /**
   * Parse JSON data from document
   * @param docData - Document data
   */
  private parseJsonData(docData: LoadedDocument): any {
    const jsonData = docData.formatParseResult?.json;
    if (!jsonData) return null;

    if (typeof jsonData === 'string') {
      try {
        return JSON.parse(jsonData);
      } catch (e) {
        logger.error('Failed to parse JSON:', e);
        return null;
      }
    }
    return jsonData;
  }

  /**
   * Process document structure with dividers, headers/footers, and images
   * @param container - Container element
   * @param docData - Document data
   * @param hasImages - Whether HTML already contains images
   */
  private processDocumentStructure(
    container: HTMLElement,
    docData: LoadedDocument,
    hasImages: boolean
  ): void {
    const parsedJson = this.parseJsonData(docData);
    if (!parsedJson) {
      return;
    }

    // Delegate all page layout processing to the PageLayoutProcessor
    this.pageLayoutProcessor.processLayout(container, parsedJson, hasImages);
  }
}
